import { useCapacitorStorage } from '@/composables/capacitor/useCapacitorStorage';
import type { CookieConfig, TokenStorageStrategy } from '../tokenContracts';
import { useCapacitorPrivacyScreen } from '../../../composables/capacitor/useCapacitorPrivacyScreen';

const REFRESH_TOKEN_EXPIRY_DAYS = 30;
const PREFS_ACCESS_TOKEN_KEY = 'cap_access_token';
const PREFS_ACCESS_TOKEN_EXPIRY_KEY = 'cap_access_token_expiry';
const PREFS_REFRESH_TOKEN_KEY = 'cap_refresh_token';
const PREFS_REFRESH_TOKEN_EXPIRY_KEY = 'cap_refresh_token_expiry';
const Storage = useCapacitorStorage();

export class CapacitorStrategy implements TokenStorageStrategy {
  private readonly cache = new Map<string, string>();
  private readonly expiry = new Map<string, Date>();

  async initialize(config: CookieConfig): Promise<void> {
    try {
      const [access, accessExpiry, refresh, refreshExpiry] = await Promise.all([
        Storage.getValue(PREFS_ACCESS_TOKEN_KEY),
        Storage.getValue(PREFS_ACCESS_TOKEN_EXPIRY_KEY),
        Storage.getValue(PREFS_REFRESH_TOKEN_KEY),
        Storage.getValue(PREFS_REFRESH_TOKEN_EXPIRY_KEY)
      ]);

      if (access && accessExpiry) {
        const exp = new Date(accessExpiry);
        if (exp > new Date()) {
          this.cache.set(config.accessTokenName, access);
          this.expiry.set(config.accessTokenName, exp);
        }
      }
      if (refresh && refreshExpiry) {
        const exp = new Date(refreshExpiry);
        if (exp > new Date()) {
          this.cache.set(config.refreshTokenName, refresh);
          this.expiry.set(config.refreshTokenName, exp);
        }
      }
    } catch (err) {
      console.error('[CapacitorStrategy] initialize failed, cache will be empty:', err);
    }
  }

  setAccessToken(token: string, expiresAt: Date, config: CookieConfig): void {
    this.cache.set(config.accessTokenName, token);
    this.expiry.set(config.accessTokenName, expiresAt);
    Storage.setValue(PREFS_ACCESS_TOKEN_KEY, token, true).catch(err =>
      console.error('[CapacitorStrategy] setAccessToken failed:', err)
    );
    Storage.setValue(PREFS_ACCESS_TOKEN_EXPIRY_KEY, expiresAt.toISOString()).catch(err =>
      console.error('[CapacitorStrategy] setAccessToken expiry failed:', err)
    );
  }

  getAccessToken(config: CookieConfig): string | undefined {
    return this.getIfNotExpired(
      config.accessTokenName,
      PREFS_ACCESS_TOKEN_KEY,
      PREFS_ACCESS_TOKEN_EXPIRY_KEY
    );
  }

  setRefreshToken(token: string, config: CookieConfig): void {
    this.cache.set(config.refreshTokenName, token);
    const expires = new Date();
    expires.setDate(expires.getDate() + REFRESH_TOKEN_EXPIRY_DAYS);
    this.expiry.set(config.refreshTokenName, expires);
    Storage.setValue(PREFS_REFRESH_TOKEN_KEY, token, true).catch(err =>
      console.error('[CapacitorStrategy] setRefreshToken failed:', err)
    );
    Storage.setValue(PREFS_REFRESH_TOKEN_EXPIRY_KEY, expires.toISOString()).catch(err =>
      console.error('[CapacitorStrategy] setRefreshToken expiry failed:', err)
    );
  }

  getRefreshToken(config: CookieConfig): string | undefined {
    return this.getIfNotExpired(
      config.refreshTokenName,
      PREFS_REFRESH_TOKEN_KEY,
      PREFS_REFRESH_TOKEN_EXPIRY_KEY
    );
  }

  private getIfNotExpired(
    cacheKey: string,
    storageTokenKey: string,
    storageExpiryKey: string
  ): string | undefined {
    const exp = this.expiry.get(cacheKey);
    if (exp && exp <= new Date()) {
      this.cache.delete(cacheKey);
      this.expiry.delete(cacheKey);
      Storage.setValue(storageTokenKey).catch(err =>
        console.error(`[CapacitorStrategy] expiry delete (${storageTokenKey}) failed:`, err)
      );
      Storage.setValue(storageExpiryKey).catch(err =>
        console.error(`[CapacitorStrategy] expiry delete (${storageExpiryKey}) failed:`, err)
      );
      return undefined;
    }
    return this.cache.get(cacheKey);
  }

  clearAll(): void {
    this.cache.clear();
    this.expiry.clear();
    Storage.setValue(PREFS_ACCESS_TOKEN_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (access) failed:', err)
    );
    Storage.setValue(PREFS_ACCESS_TOKEN_EXPIRY_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (access expiry) failed:', err)
    );
    Storage.setValue(PREFS_REFRESH_TOKEN_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (refresh) failed:', err)
    );
    Storage.setValue(PREFS_REFRESH_TOKEN_EXPIRY_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (refresh expiry) failed:', err)
    );
    const privacyScreenApi = useCapacitorPrivacyScreen();
    privacyScreenApi
      .disable()
      .catch(err => console.error('[CapacitorStrategy] clearAll (privacy screen) failed:', err));
  }
}
